import type { AccordionDetailsProps } from './accordion-details.types';
import type { FC } from 'react';
import { useMemo, useEffect, useCallback } from 'react';
import { useIsDarkMode } from 'src/common/hooks/use-is-dark-mode';
import { useIsAskEchoEnabledInCurrentRoom, useIsEchoEnabledInCurrentRoom } from 'src/features/room/room.helpers';
import { AskEchoSelectableText } from 'src/common/components/echo/ask-echo-selectable-text';
import { replaceContent, replaceContentForTaskBreakdownRooms } from 'src/features/room/helpers/content-replacer';
import { useRoomTaskBreakdownContext } from '../../../task-breakdown/components/room-task-breakdown-context';
import { StyledAccordionDetailsMain } from './accordion-details.styles';
export const AccordionDetails: FC<AccordionDetailsProps> = ({
  description: taskContent,
  title: taskTitle,
  taskNo,
  glossaryData,
  machineIP,
  attackBoxPrivateIP,
  shouldSeeVmStart,
  currentAzureLabId,
  roomCode,
  taskId
}): JSX.Element => {
  const isDarkMode = useIsDarkMode();
  const isEchoEnabled = useIsEchoEnabledInCurrentRoom();
  const isAskEchoEnabled = useIsAskEchoEnabledInCurrentRoom();
  const isEchoEnabledAndAskEchoEnabled = !!isEchoEnabled && isAskEchoEnabled;
  const {
    isTaskBreakdownSupported
  } = useRoomTaskBreakdownContext();
  const refinedContent = useMemo(() => {
    let content = replaceContent(taskContent, glossaryData, machineIP, attackBoxPrivateIP, shouldSeeVmStart, currentAzureLabId);
    if (isTaskBreakdownSupported) {
      content = replaceContentForTaskBreakdownRooms({
        content,
        taskTitle,
        taskNo,
        roomCode
      });
    }
    return content;
  }, [taskContent, glossaryData, machineIP, attackBoxPrivateIP, shouldSeeVmStart, currentAzureLabId, taskTitle, isTaskBreakdownSupported, roomCode, taskNo]);
  const handleCopyButtonClick = useCallback((event: Event) => {
    const target = (event.target as HTMLElement);
    const copyButton = target.closest('.copy-button');
    if (!copyButton) return;
    const wrapper = copyButton.closest('.copy-wrapper');
    if (!wrapper) return;
    const textElement = wrapper.querySelector('span:not(.tooltip)');
    if (!textElement) return;
    const textToCopy = textElement.textContent || '';
    navigator.clipboard.writeText(textToCopy).then(() => {
      wrapper.classList.add('show-tooltip');
      setTimeout(() => {
        wrapper.classList.remove('show-tooltip');
      }, 2000);
    }).catch(error => {
      console.error('Failed to copy text:', error);
    });
  }, []);
  useEffect(() => {
    const listener = (e: Event) => handleCopyButtonClick(e);
    document.addEventListener('click', listener, true);
    return () => {
      document.removeEventListener('click', listener, true);
    };
  }, [handleCopyButtonClick]);
  return isEchoEnabledAndAskEchoEnabled ? <AskEchoSelectableText taskId={taskId} roomCode={roomCode}>
      <StyledAccordionDetailsMain
    // biome-ignore lint/security/noDangerouslySetInnerHtml: Content is from a trusted source and is processed to replace placeholders and apply syntax highlighting. DOMParser is used for specific modifications, ensuring safe HTML construction.
    dangerouslySetInnerHTML={{
      __html: refinedContent
    }} $isTaskBreakdownSupported={!!isTaskBreakdownSupported} $isDarkMode={isDarkMode} $isAskEchoEnabled={isEchoEnabledAndAskEchoEnabled} />
    </AskEchoSelectableText> : <StyledAccordionDetailsMain
  // biome-ignore lint/security/noDangerouslySetInnerHtml: Content is from a trusted source and is processed to replace placeholders and apply syntax highlighting. DOMParser is used for specific modifications, ensuring safe HTML construction.
  dangerouslySetInnerHTML={{
    __html: refinedContent
  }} $isTaskBreakdownSupported={!!isTaskBreakdownSupported} $isDarkMode={isDarkMode} $isAskEchoEnabled={isEchoEnabledAndAskEchoEnabled} />;
};